import { useRef, useState, type FormEvent } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { site } from "@/mocks/site";

const ORIGIN = "https://tatoys.cn";
const FORM_ADDR = "https://readdy.ai/api/form/dahoceboh653ivfvo2h0";

const contactFaqs = [
  {
    question: "多久会收到回复？",
    answer: "一般 2 个工作日内回复。若话题需要进一步核实，我们会在邮件里说明进度。",
  },
  {
    question: "可以推荐别人的话题吗？",
    answer: "可以，但请附上公开来源。只做公开讨论的二次归纳，因此需要能追溯到公开作品的链接。",
  },
  {
    question: "内容有误怎么反馈？",
    answer: "在留言里写明文章标题与需要更正的具体句子，附上依据，我们会优先处理事实性偏差。",
  },
];

export default function ContactPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  useSeo({
    title: "联系我们 - 推荐话题与内容反馈｜黑料在线",
    description:
      "通过联系表单向黑料在线推荐话题、投稿或反馈内容问题。请提供公开来源链接，编辑一般在 2 个工作日内回复。",
    keywords: "黑料在线联系,推荐话题,内容反馈",
    path: "/contact",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        url: `${ORIGIN}/contact`,
        name: "联系我们",
        inLanguage: "zh-CN",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "联系我们", item: `${ORIGIN}/contact` },
          ],
        },
        mainEntity: {
          "@type": "Organization",
          name: "黑料在线",
          email: site.email,
          url: `${ORIGIN}/`,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: contactFaqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
    ],
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("提交成功，感谢你的推荐，我们会尽快查看。");
      form.reset();
      return;
    }

    payload.delete("website_alt");
    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === "OK") {
        setStatus("success");
        setMessage("提交成功，感谢你的推荐，我们会尽快查看并在 2 个工作日内回复。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMessage === "string" && serverMessage.length > 0
            ? serverMessage
            : "提交失败，请稍后重试，或直接发送邮件给我们。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功。请检查网络后重试，或直接发送邮件给我们。");
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "联系我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
            联系我们
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            推荐话题、投稿，或者反馈内容偏差，都可以通过下面的表单告诉我们。请尽量附上公开来源链接，方便我们核实。
          </p>
        </section>

        <section className="w-full px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
            <div className="rounded-card border border-background-200 bg-background-50 p-5 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950">留言给我们</h2>
              <p className="mt-2 text-sm text-foreground-600">
                带 <span className="text-primary-600">*</span> 的为必填项，留言请控制在 500 字以内。
              </p>

              <form
                ref={formRef}
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-sm font-label text-foreground-800">
                      称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-sm font-label text-foreground-800">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收我们的回复"
                      className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="topic_type" className="text-sm font-label text-foreground-800">
                    留言类型
                  </label>
                  <select
                    id="topic_type"
                    name="topic_type"
                    defaultValue="推荐话题"
                    className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="推荐话题">推荐话题</option>
                    <option value="内容投稿">内容投稿</option>
                    <option value="纠错反馈">纠错反馈</option>
                    <option value="合作洽谈">合作洽谈</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="source_url" className="text-sm font-label text-foreground-800">
                    公开来源链接
                  </label>
                  <input
                    id="source_url"
                    name="source_url"
                    type="url"
                    placeholder="https:// （可选，便于我们核实）"
                    className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="message" className="text-sm font-label text-foreground-800">
                    留言内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="说说你想推荐的话题，或者你发现的问题（最多 500 字）"
                    className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <input
                  className="website-alt-field"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                  >
                    <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                    {status === "loading" ? "提交中…" : "提交留言"}
                  </button>
                  <p className="text-xs text-foreground-500">我们不会将你的邮箱用于任何营销用途。</p>
                </div>

                {status === "success" && (
                  <p className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-checkbox-circle-line mt-0.5" />
                    {message}
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5" />
                    {message}
                  </p>
                )}
              </form>
            </div>

            <aside className="flex flex-col gap-5">
              <div className="rounded-card border border-background-200 bg-secondary-50 p-5 md:p-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950">其他联系方式</h2>
                <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                  <li className="flex items-center gap-2.5">
                    <i className="ri-mail-line text-primary-600" />
                    <a href={`mailto:${site.email}`} className="cursor-pointer hover:text-primary-700">
                      {site.email}
                    </a>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="ri-time-line text-primary-600" />
                    {site.workingHours}
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="ri-calendar-check-line text-primary-600" />
                    {site.updateNote}
                  </li>
                </ul>
              </div>

              <div className="rounded-card border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950">常见疑问</h2>
                <dl className="mt-4 space-y-4">
                  {contactFaqs.map((f) => (
                    <div key={f.question}>
                      <dt className="font-heading text-sm font-semibold text-foreground-950">{f.question}</dt>
                      <dd className="mt-1 text-sm leading-relaxed text-foreground-600">{f.answer}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}